<template>
  <BaseWidget
    :class="['activities-timeline']"
    :loading="loading"
    :content-only="contentOnly"
    :widget-options="widgetMenuOptions" 
    :override-options="true"
    @refresh-data="refreshData" 
    @set-config="setConfig"
    v-on="$listeners"
  >
    <template v-slot:header v-if="!contentOnly">
      <div class="title">
        <span class="title-icon">
        </span>
        {{ title }}
      </div>
      <div class="description">{{ filterText }}, {{ usersText }}</div>
    </template>
    <template v-slot:content>
      <div class="list-wrap">
        <ul role="list" class="activity-list">
          <ActivitiesTimelineItem v-for="(item, index) in activityItems" :key="item.id + '-' + index" :item="item" :size="size" />
        </ul>
        <a v-if="nextUrl" class="load-more-btn" href="#" @click.prevent="loadMore()">{{ $gettext('Load more') }}</a>
        <span v-if="!nextUrl && (activityItems && activityItems.length > 0)" class="load-more-btn all-items">{{ $gettext('Displaying all items') }}</span>
        <span v-if="activityItems === null || activityItems.length === 0" class="load-more-btn">{{ $gettext('We could not find any results.') }}<br>{{ $gettext('Try again with different filters.') }}</span>
      </div>
    </template>
  </BaseWidget>
</template>

<script>
import { mapGetters } from 'vuex';

import DashboardService from "@/store/services/dashboard.service.js";
import BaseWidget from "@/components/dashboard/widgets/BaseWidget"
import ActivitiesTimelineItem from "@/components/dashboard/widgets/ActivitiesTimelineItem"

export default {
  name: 'timeline-widget',
  components: { BaseWidget, ActivitiesTimelineItem },
  props: {
    widget: {
      type: Object,
      default: () => {}
    },
    size: {
      type: String,
      default: 'sm'
    }
  },
  data() {
    return {
      contentOnly: false,
      loading: true,
      title: '',
      subtitle: '',
      activityItems: null,
      nextUrl: null,
      config: { object_type: '', user: {} },
      filters: {
        object_type: '',
        created_by_in: '',
        type: '',
        company_profile: null,
        cost_id: null
      }
    }
  },
  computed: {
    ...mapGetters({ 
      getCompanyId: 'main/getCompanyId'
    }),
    widgetMenuOptions () {
      if (this.loading) return []
      return [
        { name: this.$gettext('Content type'), submenu: true,
          items: [
            { name: this.$gettext('All content'), event: { name: 'set-config', config: { object_type: 'all' } } },
            { name: this.$gettext('Costs'), event: { name: 'set-config', config: { object_type: 'cost' } } },
            { name: this.$gettext('Invoices'), event: { name: 'set-config', config: { object_type: 'invoice' } } },
            { name: this.$gettext('Quotation'), event: { name: 'set-config', config: { object_type: 'quotation' } } },
            { name: this.$gettext('Trips'), event: { name: 'set-config', config: { object_type:  'trip' } } },
            { name: this.$gettext('Contacts'), event: { name: 'set-config', config: { object_type:  'contact' } } }
          ]
        },
        { name: this.$gettext('Users'), submenu: true,
          items: [
            { name: this.$gettext('All users'), event: { name: 'set-config', config: { user: 'all' } } },
            ...this.$store.getters['dashboard/getAvailableUsers']
          ]
        },
        { separator: true },
        { name: this.$gettext('Refresh data'), icon: 'icon-refresh', event: { name: 'refresh' } }
      ]
    },
    filterText () {
      let text = this.$gettext('All content')
      if (this.config.object_type !== '') {
        text = this.$gettext('Data from')
        switch (this.config.object_type) {
          case 'cost':
            text = text + ' ' + this.$gettext('costs')
            break;
          case 'invoice':
            text = text + ' ' + this.$gettext('invoices')
            break;
          case 'quotation':
            text = text + ' ' + this.$gettext('quotations')
            break;
          case 'hour':
            text = text + ' ' + this.$gettext('hours')
            break;
          case 'trip':
            text = text + ' ' + this.$gettext('trips')
            break;
          case 'contact':
            text = text + ' ' + this.$gettext('contacts')
            break;
        }
      }
      return text
    },
    usersText () {
      let text = (this.config.user && this.config.user.display_name) ? this.config.user.display_name : this.$gettext('all users')
      return text
    }
  },

  created () {
    this.filters.company_profile = this.getCompanyId
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'timeline/TIMELINE_LIST_REFRESH') {
        this.refreshData()
      }
    })
  },
  beforeDestroy() {
    this.unsubscribe()
  },
  mounted () {
    this.setupWidget()
    this.setObjectType(this.widget.config)
  },
  methods: {
    setConfig (config) {
      if (config.object_type) this.setObjectType(config)
      if (config.user) this.setObjectType(config)
      this.$emit('save-config', this.config)
    },
    setObjectType (config) {
      if (config.object_type) {
        this.config.object_type = (config.object_type && config.object_type !== 'all') ? config.object_type : ''

        // TODO return invoice_payment types also when type = 'invoice'
        // so we dont have to nullify this
        if (this.config.object_type === 'invoice' && config.invoice_id) {
          this.config.object_type = null 
        }
      } 
      if (config.user) {
        this.config.user = (config.user && config.user !== 'all') ? config.user : ''
      }
      if (config.cost_id) {
        this.contentOnly = true
        this.config.cost_id = config.cost_id
      }
      if (config.invoice_id) {
        this.contentOnly = true
        this.config.invoice_id = config.invoice_id
      }
      if (config.quotation_id) {
        this.contentOnly = true
        this.config.quotation_id = config.quotation_id
      }
      this.setFilters()
      this.refreshData()
    },
    setFilters () {
      this.filters.object_type = this.config.object_type
      this.filters.created_by_in = this.config.user.id
      this.filters.cost_id = this.config.cost_id
      this.filters.invoice = this.config.invoice_id
      this.filters.quotation = this.config.quotation_id
    },
    refreshData () {
      this.getData()
    },
    getData () {
      this.loading = true
      this.nextUrl = null
      DashboardService.activitiesTimeline(this.filters).then((response) => {
        this.nextUrl = response.data.next
        this.activityItems = response.data.results
        this.loading = false
      })
    },
    loadMore () {
      this.loading = true
      DashboardService.moreActivities(this.nextUrl).then((response) => {
        this.nextUrl = response.data.next
        this.activityItems = this.activityItems.concat(response.data.results)
        this.loading = false
      })
    },
    setupWidget () {
      this.title = this.$gettext('Account timeline')
    }
  }
}
</script>

<style lang="scss">
.activities-timeline {
  overflow: hidden;
  height: 100%;
  margin-bottom: 20px;
  .list-wrap { 
    flex-direction: column;
    margin: -7px;
    flex: 1;
    width: 100%;
  }
  ul.activity-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .load-more-btn {
    display: block;
    text-align: center;
    padding: 10px;
    padding-bottom: 20px;
    font-size: 14px;
    &.all-items { color: #cacaca; }
  }
}
</style>